import React from 'react'
import { useMemo } from 'react'
import { JrControlsMarkdown, type MarkdownOverrideBundle } from '@mission.io/question-views'
import TextToSpeech from '../TextToSpeech'
import { useSelector } from 'react-redux'
import { getCurrentStudentTextToSpeechOptions } from '../../store/selectors/sharedSelectors'
import { TEXT_TO_SPEECH_OPTIONS } from '@mission.io/mission-toolkit/constants'

export function MarkdownTextToSpeech({
	children,
	className,
	preparser,
	customComponents,
}: {
	children: string,
	className?: string,
	preparser?: ?(text: string) => string,
	customComponents?: ?MarkdownOverrideBundle,
}): React$Node {
	const markdown = useMemo(() => preparser?.(children) ?? children, [children, preparser])
	const isTextToSpeechEnabled =
		useSelector(getCurrentStudentTextToSpeechOptions) !== TEXT_TO_SPEECH_OPTIONS.OFF

	return (
		<JrControlsMarkdown
			textToSpeech={isTextToSpeechEnabled ? TextToSpeechWithWrappers : null}
			className={className}
			overrides={customComponents}>
			{markdown}
		</JrControlsMarkdown>
	)
}

/**
 * TextToSpeechWithWrappers - a wrapper of "text to speech" which hides the "text to speech" button if "text to speech" is not enabled
 *
 * @param {Object} props - the react props
 * @param {React$Node} props.children - the text to read
 * @param {(event: TouchEvent | MouseEvent) => void} props.onStartSpeech - a callback used when reading is initiated
 * @param { () => void} props.onEndSpeech - a callback used when reading has finished
 *
 * @return {React$Node}
 */
export function TextToSpeechWithWrappers({
	children,
	onStartSpeech,
	onEndSpeech,
}: {
	children: React$Node,
	onStartSpeech?: (event: TouchEvent | MouseEvent) => void,
	onEndSpeech?: () => void,
}): React$Node {
	const isTextToSpeechEnabled =
		useSelector(getCurrentStudentTextToSpeechOptions) !== TEXT_TO_SPEECH_OPTIONS.OFF

	if (!isTextToSpeechEnabled) {
		return null
	}
	return (
		<TextToSpeech position="start" onStartSpeech={onStartSpeech} onEndSpeech={onEndSpeech}>
			{children}
		</TextToSpeech>
	)
}
